import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { DealerStateContext } from "@insite/client-framework/Store/Data/Dealers/DealersSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext } from "react";
import { css } from "styled-components";
import * as cssLinter from "css";
import parse from "html-react-parser";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";

const enum fields {
    displayBannerAsGlobal = "displayBannerAsGlobal",
    bannerContent = "bannerContent",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.displayBannerAsGlobal]: string;
        [fields.bannerContent]: string;
        [fields.customCSS]: string;
    };
}

type Props = OwnProps;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const defaultCustomCss = `
.simpleGlobalBanner {
	padding: 14px 40px;
    border-radius: 8px;
    background: url(/userfiles/red-frame.png) repeat;
	background-size: auto;
    background-position: center;
    margin-bottom: 24px;
}
.text-content {
	display: flex;
	flex-direction: column;
	gap: 8px;
    justify-content: center;
}
.heading {
    font-size: 24px;
    line-height: 130%;
    font-weight: 600;
    margin: 0;
    color:#fff;
}
.link-wrapper a {
    font-size: 16px;
    font-weight: 600;
    line-height: 130%; 
}
.link-wrapper a,
.link-wrapper a [class*="StyledTypography"],
.link-wrapper a:hover,
.link-wrapper a:hover [class*="StyledTypography"] {
	color: #fff;
	text-decoration: underline;
}
.image  img {
    display: flex;
}
@media screen and (max-width: 767px){
    .image {
        display: none;
    }
}
@media screen and (min-width: 768px){ 
    .simpleGlobalBanner {
        padding: 14px 48px;
        display: flex;
        gap: 24px;
    }
    .image {
        width: 52px;
    }
    .image img {
        width: 100%;
        height: auto;
    }
    .text-content {
        flex-direction: row;
        gap: 24px;
        align-items: center;
    }
    .heading {
        font-size: 32px;
    }
    .link-wrapper {
        white-space: nowrap;
    }
}
`;


const BranchSpecificBanner: React.FC<Props> = ({
    fields,
}) => {
    const { value: dealer } = useContext(DealerStateContext);

    const hasDealerBanner = dealer?.properties?.["dealerBranchBanner"] &&
                            dealer.properties["dealerBranchBanner"].trim() !== "";
    const hasGlobalBanner = !!fields.bannerContent;

    if (!hasDealerBanner && !hasGlobalBanner) {
        return null;
    }
    
    const customCssWrapper = {
        css: css`
            margin-bottom: 24px;
            ${fields.customCSS}
            @media print {
                .simpleGlobalBanner {
                    padding: 14px 48px;
                    display: flex;
                    gap: 24px;
                }
                .image {
                    width: 52px;
                }
                .link-wrapper {
                    display: none;
                }      
            }
        `,
    };


    return (
        <StyledWrapper {...customCssWrapper}>
            {hasDealerBanner ? (
                <StyledWrapper
                    dangerouslySetInnerHTML={{
                        __html: dealer!.properties["dealerBranchBanner"],
                    }}
                />
            ) : (
                fields.displayBannerAsGlobal && (
                    <StyledWrapper>
                        {parse(fields.bannerContent, parserOptions)}
                    </StyledWrapper>
                )
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: BranchSpecificBanner,
    definition: {
        group: "Common",
        displayName: "Branch Specific Banner",
        fieldDefinitions: [
            {
                name: fields.displayBannerAsGlobal,
                displayName: "Display Banner As Global",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 0,
                tab: basicTab,
            },
            {
                name: fields.bannerContent,
                displayName: "Banner Content",
                editorTemplate: "RichTextField",
                extendedConfig: { height: 200 },
                defaultValue: `
                    <div class="simpleGlobalBanner">
                        <div class="image">
                            <img src="/userfiles/spark-common-red-icon.svg">
                        </div>
                        <div class="text-content">
                            <div>
                                <h2 class="heading">Branch name grand opening on June 23rd!</h2>
                            </div>
                            <div class="link-wrapper">
                                <a href="#">Learn more</a>
                            </div>
                        </div>
                    </div>
                `,
                fieldType: "Contextual",
                sortOrder: 1,
                tab: basicTab,
                isVisible: widget => widget.fields.displayBannerAsGlobal,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                sortOrder: 2,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
